import { useI18n } from '../../contexts/I18nContext';
import { CountUp } from '../dashboard/charts';
import type { ReportFilters } from '../../lib/taskReportsApi';
import { useOverviewReport } from '../../hooks/useTaskReports';
import { formatHours, formatPercent, SectionState } from './common';

// KPI card strip fed by /api/reports/tasks/overview.

function Kpi({
  label,
  value,
  format,
  fallback,
}: {
  label: string;
  value: number | null;
  format: (n: number) => string;
  fallback?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">
        {value === null ? (fallback ?? '—') : <CountUp value={value} format={format} />}
      </p>
    </div>
  );
}

export function ReportKpiCards({ filters }: { filters: ReportFilters }) {
  const { t } = useI18n();
  const { data, isLoading, isError } = useOverviewReport(filters);

  if (isLoading || isError) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <SectionState isLoading={isLoading} isError={isError} />
      </div>
    );
  }
  if (!data) return null;

  const int = (n: number) => String(Math.round(n));
  // Accuracy is a raw estimate/spent ratio; clamp display to something sane.
  const accuracy = data.estimateAccuracy === null ? null : Math.min(data.estimateAccuracy, 9.99);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Kpi label={t('reports.kpi.spentHours')} value={data.spentHours} format={formatHours} />
      <Kpi
        label={t('reports.kpi.estimatedHours')}
        value={data.estimatedHours}
        format={formatHours}
      />
      <Kpi label={t('reports.kpi.estimateAccuracy')} value={accuracy} format={formatPercent} />
      <Kpi label={t('reports.kpi.created')} value={data.createdCount} format={int} />
      <Kpi label={t('reports.kpi.completed')} value={data.completedCount} format={int} />
      <Kpi label={t('reports.kpi.onTimeRate')} value={data.onTimeRate} format={formatPercent} />
      <Kpi label={t('reports.kpi.overruns')} value={data.overrunTaskCount} format={int} />
      <Kpi label={t('reports.kpi.activePeople')} value={data.activePeople} format={int} />
    </div>
  );
}
